import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import notify from 'devextreme/ui/notify';
import DropDownButton from 'devextreme-react/drop-down-button';
import TextArea from 'devextreme-react/text-area';
import Gallery from 'devextreme-react/gallery';
import {Button} from 'devextreme-react/button';
import {LoadIndicator} from 'devextreme-react/load-indicator';
import {Popup} from 'devextreme-react/popup';
import './home.scss';
import {useComm} from '../../contexts/communication';
import {useConversations} from "../../contexts/conversation";
import FileUploader from 'devextreme-react/file-uploader';
import {useIsMount} from '../../utils/isMountHook';
import * as signalR from '@microsoft/signalr';
import {useApi, apiUrl} from "../../api/api-methods";
import {formatBytes} from "../../utils/utils.num";
import {fileTypeIcon} from "../../utils/file-type-icons"
import {TextBox, Button as TextBoxButton} from "devextreme-react/text-box";
import {confirm} from 'devextreme/ui/dialog';
import {Tooltip} from "devextreme-react";
import {useAuth} from "../../contexts/auth";

const buttonDropDownOptions = {width: 200};

export default function Home() {
  // -------------------------------------------------------------------------------------------------------------------
  // hooks
  const isMount = useIsMount();
  const comm = useComm();
  const navigate = useNavigate();
  const {conversationId} = useParams();
  const [searchParams] = useSearchParams();
  const conversationCtx = useConversations();
  const fileUploader = useRef(null);
  const clickedImage = useRef(null);
  const chatHeader = useRef(null)
  const api = useApi();
  const {user} = useAuth();
  // -------------------------------------------------------------------------------------------------------------------
  // state
  const [connection, setConnection] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [models, setModels] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadIndicatorVisible, setLoadIndicatorVisible] = useState(false);
  const [buttonText, setButtonText] = useState('Send');
  const [popupVisible, setPopupVisible] = useState(false);
  const [settings, setSettings] = useState({allowedContentTypes: ['image/*'], maxFileSize: 100480, default: true});
  const [nameEditing, setNameEditing] = useState(false);
  const [isWithContext, setIsWithContext] = useState(!!searchParams.get('my-gpt'));
  const [gptContextId, setGptContextId] = useState(searchParams.get('my-gpt'));
  const [gptContextName, setGptContextName] = useState(searchParams.get('my-gpt-name'));
  const [gptContext, setGptContext] = useState(null); //todo
  // -------------------------------------------------------------------------------------------------------------------
  // methods
  function onInit() {
    console.log('onInit', searchParams.get('my-gpt'));
    if (!connection) {
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(apiUrl('streamchat'), {accessTokenFactory: () => api.token})
        .withAutomaticReconnect()
        .build();
      setConnection(newConnection);
    }

    if (!models.length) api.gptModel.getActive()
      .then(ms => {
        if (!Array.isArray(ms) || !ms.length) return;
        setModels(ms);
        if (!selectedModel) setSelectedModel(ms[0]);
      });

    if (settings?.default) api.file.uploadSettings().then(setSettings);

    if (isWithContext && gptContext === null) {
      api.context.getContext(gptContextId).then(ctx => setGptContext(ctx));
    }
    if (!searchParams.get('my-gpt') && !gptContext) {
      setIsWithContext(false);
    }
    if (!!searchParams.get('my-gpt')) {
      evaluatePermission(+searchParams.get('my-gpt')).then(allowed => {
        if (!allowed) kickOut();
      });
    }
  }

  const fetchConversation = useCallback(async (id) => {
    console.log('fetchConversation', id);
    await api.conversation.getFullById(id)
      .then(chat => {
        setConversation(chat);
        setMessages(chat.messages.map(m => ({
          text: m.message,
          isBot: m.isBot,
          atts: m.attachments.map(a => ({
            success: true,
            src: apiUrl(`file/download/${a.id}/${a.fileName}`),
            name: a.originalName,
            type: a.contentType,
            size: a.size
          }))
        })));
        if (chat.contextId) {
          setIsWithContext(true);
          setGptContextId(chat.contextId);
          api.context.getContext(chat.contextId).then(ctx => {
            setGptContext(ctx);
            setGptContextName(ctx.name);
            evaluatePermission(ctx.id).then(allowed => {
              if (!allowed) kickOut();
            });
            console.log('context fetched', ctx);
          });
          console.log('conversation fetched with context', chat);
        } else {
          clearGptContext();
        }
      })
      .catch(() => {
        setConversation(null);
        navigate('home');
      });
  }, [api, navigate]);

  function clearGptContext() {
    if (!!searchParams.get('my-gpt')) return;
    setIsWithContext(false);
    setGptContextId(null);
    setGptContextName(null);
    setGptContextId(null);
  }

  async function getOrCreateConversation() {
    if (conversation) return conversation;

    const chat = await api.conversation.startNew(
      selectedModel.id,
      prompt,
      files.map(f => f.uploadId).filter(id => !!id),
      gptContextId);
    setConversation(chat);
    return chat;
  }

  async function performSendPrompt(myGptRelevantDocs) {
    setButtonText('🤖');
    await getOrCreateConversation();
    const atts = files
      .filter(f => f.success)
      .map(f => ({...f, name: f.name, size: f.size, type: f.type, src: URL.createObjectURL(f)}));

    setMessages([...messages,
      {text: prompt, isBot: false, relevantContext: relevantDocsToPrompt(myGptRelevantDocs), atts},
      {text: '', isBot: true}]);
  }

  async function sendPrompt() {
    if (!prompt.trim().length) {
      comm.error('Please type something to send.');
      return;
    }
    setLoadIndicatorVisible(true);
    const gptContextRelevantDocs = isWithContext ? await api.context.getRelevantDocs(gptContextId, prompt) : null;
    console.log('gptContextRelevantDocs', gptContextRelevantDocs);
    await performSendPrompt(gptContextRelevantDocs);
  }

  function relevantDocsToPrompt(relevantDocs) {
    if (!relevantDocs) return '';

    const textualContext = relevantDocs.map(doc => ` - ${doc.content}\n\n`).join('\n');
    return `Context:\n\n"""\n${textualContext}\n"""\n\n`;
  }

  const fileUpdate = useCallback(({file, request}) => {
    const update = !request.status
      ? {status: 'Uploading...', src: URL.createObjectURL(file)}
      : request.status >= 299
        ? {success: false, status: `Upload failed: ${request.response}`}
        : {success: true, uploadId: request.response, status: 'Uploaded'};
    Object.assign(file, update);
    setFiles([...files]);
  }, [files]);

  const fileRemove = useCallback((file, ix) => {
    new Promise((resolve, reject) => file.uploadId ? resolve(file) : reject(file))
      .then(f => api.file.remove(f.uploadId, conversationId))
      .catch((f) => 'not uploaded OR delete failed')
      .finally(() => {
        setFiles([...files.slice(0, ix), ...files.slice(ix + 1)]);
      });
  }, [api, conversationId, files]);

  const conversationRename = useCallback(({event}) => {
    const newName = event.target.value.trim();
    if (!newName) return;
    const updated = {...conversation, summary: newName};
    conversationCtx.rename(updated);
    setConversation(updated);
    setNameEditing(false);
    chatHeader.value = newName;
  }, [conversation, conversationCtx]);

  async function reactWithChatCompletion(prompt, promptItem) {

    if (conversation && +conversationId !== conversation.id) {
      navigate(`/home/${conversation.id}`);
    }
    conversationCtx.moveToTop(conversation);

    await connection.start();

    const [lastMessage] = messages.slice(-1);
    const messageFiles = files.filter(i => i.uploadId).map(i => ({
      fileName: i.uploadId,
      contentType: i.type,
      originalName: i.fileName
    }));
    const relevantContext = promptItem ? promptItem.relevantContext || '' : '';
    const systemMessage = gptContext ? gptContext.description : '';
    setPrompt('');
    setFiles([]);
    let isFirst = true;

    connection.stream('Streamlined', conversation.id, prompt, messageFiles, relevantContext, systemMessage).subscribe({
      next: (item) => {
        console.log('StreamData', item);
        lastMessage.text += item;
        lastMessage.streaming = true;
        setMessages([...messages]);
        if (isFirst) {
          document.querySelector('.dx-card').scrollTo(0, document.querySelector('.dx-card').scrollHeight);
          isFirst = false;
        }
      }, complete: () => {
        console.log('Stream completed');
        connection.stop();

        if (files.length) {
          setFiles([]);
          fileUploader.current.instance.reset();
        }
        setLoadIndicatorVisible(false);
        setButtonText('Send');
      }, error: (err) => {
        console.log('Streaming error: ', err);
        comm.error(`Streaming error: ${err}`);
        setLoadIndicatorVisible(false);
        setButtonText('Send');
      }
    });
  }

  async function onEnterKey(e) {
    // ctrl+enter for new line
    if (e.event.ctrlKey) {
      // e.event.preventDefault();
      // e.event.originalEvent.preventDefault();
      setPrompt(prompt + '\n');
    } else {
      await sendPrompt();
    }
  }

  async function onKeyDown(e) {
    if (!e.event.ctrlKey && e.event.key === 'Enter') {
      e.event.preventDefault();
    }
  }

  async function onFilesValueChanged(e) {
    for (const file of e.value) {
      if (file.status) continue;
      Object.assign(file, {status: 'Not uploaded. File type not allowed.', success: false});
    }
    setFiles(e.value);
  }

  function onConversationImageClicked(e) {
    console.log('onConversationImageClicked', e);
    clickedImage.current = e.target;
    setPopupVisible(true);
  }

  function onPopupImageShown(e) {
    if (!clickedImage.current) return;
    document.querySelector('#imagePreviewPopup').src = clickedImage.current.src;
  }

  function onConversationDelete() {
    confirm("Conversation will be permanently deleted. Are you sure?", "Delete confirmation")
      .then(confirmed => {
        if (!confirmed) return;
        conversationCtx.remove({id: +conversationId});
        navigate('/home/');
      })
  }

  const renderAttachment = useCallback((file, ix) => {
    const {name, size, type, src, uploadId, status, success} = file;
    const canDelete = !!uploadId || success === false;
    const onClick = canDelete
      ? fileRemove.bind(this, file, ix)
      : () => setFiles([...files.slice(0, ix), ...files.slice(ix + 1)]);
    const image = src && type && type.startsWith('image/');
    return (<div key={src || uploadId || name} className={"attachment" + (success ? " success" : " error")}>
      <div className="attachment-icon">
        {image && success && <img src={src} onClick={onConversationImageClicked} alt={name}/>}
        {!image && success && <a href={src} download={name}><i className={fileTypeIcon(type)}/></a>}
        {!success && <i className="dx-icon dx-icon-warning"/>}
      </div>
      <div className="attachment-desc">
        <div className="attachment-name">{name}</div>
        <div className="attachment-size">{formatBytes(size)}</div>
        <div className="attachment-status">{status}</div>
        {canDelete && <Button icon="close" className="close-icon" onClick={onClick}/>}
      </div>
    </div>);
  }, [files, fileRemove]);

  function toggleClass(e) {
    e.target.className = (e.target.className === 'adjust-size') ? '' : 'adjust-size';
  }

  async function evaluatePermission(ctxId) {
    if (!ctxId) return true;
    const ctx = await api.context.getAccess(ctxId);
    if (!ctx || !ctx.length) return true; // empty users list means public context
    for (const c of ctx) {
      if (c.userId === user.id) return true;
    }
    return false;
  }

  function kickOut() {
    notify('You are not allowed to access this GPT model.', 'error', 4000);
    setIsWithContext(false);
    setGptContextId(null);
    setGptContextName(null);
    setGptContextId(null);
    navigate('/home');
  }

  // -------------------------------------------------------------------------------------------------------------------
  // effects

  const chatDetail = useMemo(() => {
    if (!conversation) return null;
    const agg = conversation?.messages?.map(m => m.attachments).flat()
      .reduce((acc, cur) => ({
        size: acc.size + cur.size, count: acc.count + 1
      }), {size: 0, count: 0});
    return {
      created: new Date(conversation.created).toLocaleString(),
      updated: new Date(conversation.lastUpdate).toLocaleString(),
      messages: messages.length,
      userMessages: messages.filter(m => !m.isBot).length,
      files: agg?.count ?? 0,
      size: agg?.size ?? 0
    }
  }, [conversation, messages])

  useEffect(() => {
    if (!chatHeader) return;
    if (nameEditing) {
      chatHeader.current.instance.focus();
    } else {
      // chatHeader.value = conversation?.summary;
      chatHeader.current.instance.option('value', conversation?.summary || 'New conversation');
    }

  }, [chatHeader, nameEditing, conversation])


  useEffect(() => {
    if (isMount) return;
    const [prevMessage, lastMessage] = messages.slice(-2);
    if (!lastMessage || lastMessage.streaming || (lastMessage.isBot && lastMessage.text) || !prevMessage) return;
    void reactWithChatCompletion(prevMessage.text, prevMessage);
  }, [messages, isMount]);

  useEffect(() => {
    if (isMount) return;
    console.log('conversationId useEffect', conversationId);
    if (+conversationId) {
      void fetchConversation(+conversationId);
    } else {
      setConversation(null);
      setMessages([]);
      clearGptContext();
    }
  }, [isMount, conversationId, fetchConversation]);

  useEffect(() => conversationCtx.select(+conversationId || null), [conversationId, conversationCtx]);

  useEffect(onInit);

  // -------------------------------------------------------------------------------------------------------------------
  return (
    <React.Fragment>
      <div className={'content-block home-height-stretch'}>
        {/* <div className={'dx-card responsive-paddings'}> */}
        <div className={'dx-card'}>
          <div className={'dx-card-header'}>
            <div className={'dx-card-header-content'}>
              {/*<h5>{conversation?.summary || 'Conversation'}</h5>*/}
              <TextBox
                id="conversation-header-rename-textbox"
                className={"header" + (nameEditing ? " edit" : "")}
                ref={chatHeader}
                readOnly={!nameEditing}
                focusStateEnabled={nameEditing}
                hoverStateEnabled={nameEditing}
                defaultValue={conversation?.summary || 'New conversation'}
                onEnterKey={conversationRename}
                valueChangeEvent="input"
                maxLength={48}
                width={'100%'}>
                <TextBoxButton
                  name="edit"
                  location="after"
                  options={{
                    visible: !!conversation,
                    disabled: false,
                    icon: 'edit',
                    onClick: () => setNameEditing(!nameEditing)
                  }}/>
                <TextBoxButton
                  name="delete"
                  location="after"
                  options={{
                    visible: !!conversation,
                    disabled: nameEditing,
                    icon: 'trash',
                    onClick: onConversationDelete
                  }}/>
              </TextBox>
              {isWithContext && <Button text={gptContextName} type="success" stylingMode="outlined"
                                        disabled={false}
                                        width="auto"
                                        elementAttr={{style: 'min-width: 10vw'}}
                                        onClick={() => notify('You are using custom GPT for this conversation: ' + gptContextName, 'success')}/>}
              <DropDownButton
                text={selectedModel?.title || 'Select model'}
                //icon="bookmark"
                dropDownOptions={buttonDropDownOptions}
                dataSource={models}
                keyExpr={'id'}
                displayExpr={'title'}
                disabled={!!conversationId}
                onItemClick={(e) => setSelectedModel(e.itemData)}
              />
              {chatDetail && <i className="dx-icon dx-icon-description" id="conversation-detail"/>}
            </div>
            {chatDetail && <Tooltip
              target="#conversation-detail"
              showEvent="dxhoverstart"
              hideEvent="dxhoverend"
            >
              <div className="conversation-detail-tooltip">
                <div><label>Created</label>{chatDetail.created}</div>
                <div><label>Last update</label>{chatDetail.updated}</div>
                <div><label>User messages</label>{chatDetail.userMessages}</div>
                <div><label>Attachments</label>{chatDetail.files}</div>
                <div><label>Attachment Size</label>{formatBytes(chatDetail.size, 0)}</div>
              </div>
            </Tooltip>}
          </div>
          <div className={'dx-card-block'}>
            <div className={'conversation'}>
              {messages.map((item, index) => (
                <div key={index}>
                  {item.atts && item.atts.length > 0 && (
                    <div className="attachments">
                      <Gallery
                        items={item.atts}
                        height={120}
                        width={'100%'}
                        initialItemWidth={320}
                        focusStateEnabled={false}
                        loop={false}
                        showIndicator={false}
                        showNavButtons={true}
                        itemRender={renderAttachment}
                      />
                    </div>)}
                  <div className={`conversation-item ${item.isBot ? 'bot' : 'user'}`}>
                    <div className={'image-container'}>
                      <div className={'user-image'}/>
                    </div>
                    <div className={'conversation-item-text'} dangerouslySetInnerHTML={{__html: item.text}}></div>
                  </div>
                </div>
              ))}
              <div id="anchor"></div>
            </div>
          </div>
        </div>
        <div className={'user-input'}>
          {files.length > 0 && (
            <div className="attachments">
              <Gallery
                items={files}
                height={120}
                width={'100%'}
                initialItemWidth={320}
                loop={false}
                focusStateEnabled={false}
                showIndicator={false}
                showNavButtons={true}
                itemRender={renderAttachment}
              />
            </div>)}
          <div className={'input-wrapper'}>
            <Button
              id={'attach-file-button'}
              icon="attach"
              type="normal"
            />
            <TextArea value={prompt} placeholder={'Type here... (ctrl+enter for new line)'} autoResizeEnabled={true}
                      valueChangeEvent="input"
                      stylingMode="filled"
                      onEnterKey={onEnterKey}
                      onKeyDown={onKeyDown}
                      disabled={loadIndicatorVisible}
                      onValueChanged={e => setPrompt(e.value)}>
            </TextArea>
            <Button width={100}
                    id="sendButton"
                    type="default"
                    stylingMode="contained"
                    disabled={loadIndicatorVisible || !selectedModel}
                    icon={'spinright'}
                    visible={!loadIndicatorVisible}
                    hint={'Send message'}
                    onClick={sendPrompt}/>
            {/*<span className="dx-button-text">{buttonText}</span>*/}
            <LoadIndicator className="button-indicator" visible={loadIndicatorVisible}/>
          </div>
          <div>
            <FileUploader
              ref={fileUploader}
              disabled={loadIndicatorVisible}
              visible={false}
              dialogTrigger="#attach-file-button"
              accept={settings.allowedContentTypes.join(',')}
              maxFileSize={settings.maxFileSize}
              uploadMode="instantly"
              uploadUrl={apiUrl(`file/upload/${conversationId || ''}`)}
              multiple={true}
              onValueChanged={onFilesValueChanged}
              onUploadStarted={fileUpdate}
              onUploadError={fileUpdate}
              onUploaded={fileUpdate}
              uploadHeaders={{Authorization: `Bearer ${api.token}`}}
              showFileList={false}
              value={files}/>
          </div>
          <div className={'footer'}>
            Copyright © 2019-{new Date().getFullYear()} BotX s.r.o. Build with 💙 <a
            href="https://www.botx.cloud/">botx.cloud</a>
          </div>
        </div>
      </div>
      <Popup
        visible={popupVisible}
        onHiding={() => setPopupVisible(false)}
        hideOnOutsideClick={true}
        showCloseButton={true}
        onShown={onPopupImageShown}
        resizeEnabled={true}
        dragEnabled={true}
        title={clickedImage.current?.alt}>
        <div className="popup-content">
          <img id="imagePreviewPopup" className="adjust-size" alt="preview" onClick={toggleClass}/>
        </div>
      </Popup>
    </React.Fragment>
  )
}
